import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, CalendarDays, CheckCircle2, Plus } from 'lucide-react';
import { ACADEMIC_CONTENT_2026 } from '../data/academicContent';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

export default function StudySelector({ onAddTopic }) {
  const [abordagemAtiva, setAbordagemAtiva] = useState(ACADEMIC_CONTENT_2026[0]);
  const [expandidos, setExpandidos] = useState({});
  const [topicsStatus, setTopicsStatus] = useState({});

  useEffect(() => {
    fetchTopicsStatus();
  }, []);

  const fetchTopicsStatus = async () => {
    try {
      const response = await api.get('/topics');
      const topics = response.data || [];
      const status = {};
      topics.forEach(t => {
        status[t.title] = {
          id: t.id,
          revision_count: t.revision_count || 0,
          next_date: t.next_suggested_date
        };
      });
      setTopicsStatus(status);
    } catch (error) {
      console.error('Erro ao buscar status:', error);
    }
  };

  const toggleCategoria = (titulo) => {
    setExpandidos(prev => ({ ...prev, [titulo]: !prev[titulo] }));
  };

  const getRevisionBadge = (topico) => {
    const status = topicsStatus[topico];
    if (!status) return { label: 'R0', color: 'bg-slate-100 text-slate-600' };
    const count = status.revision_count || 0;
    if (count === 0) return { label: 'R0', color: 'bg-slate-100 text-slate-600' };
    if (count === 1) return { label: 'R1', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
    if (count === 2) return { label: 'R2', color: 'bg-blue-100 text-blue-700 border-blue-200' };
    return { label: 'R3+', color: 'bg-green-100 text-green-700 border-green-200' };
  };

  const handleScheduleRevision = async (topico, categoria) => {
    const status = topicsStatus[topico];
    
    if (!status) {
      // Criar tópico primeiro
      try {
        const response = await api.post('/topics', {
          title: topico,
          specialty: categoria,
          scheduled_date: new Date().toISOString()
        });
        toast.success(`Tópico "${topico}" adicionado!`);
        fetchTopicsStatus();
      } catch (error) {
        toast.error('Erro ao criar tópico');
      }
      return;
    }

    // Agendar revisão
    try {
      const nextCycle = (status.revision_count || 0) + 1;
      const response = await api.post(`/topics/${status.id}/revision?current_cycle=${nextCycle}`);
      toast.success(response.data.message);
      fetchTopicsStatus();
    } catch (error) {
      toast.error('Erro ao agendar revisão');
    }
  };

  return (
    <Card className="shadow-lg border-2 border-blue-200 dark:border-blue-800">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl text-slate-900 dark:text-slate-100">
          Cronograma de Estudos 2026
        </CardTitle>
        <p className="text-sm text-slate-500">Selecione sua abordagem de revisão</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Seletor de Abordagem */}
        <Select 
          value={abordagemAtiva.id} 
          onValueChange={(v) => setAbordagemAtiva(ACADEMIC_CONTENT_2026.find(a => a.id === v))}
        >
          <SelectTrigger className="w-full h-12 text-base font-semibold">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACADEMIC_CONTENT_2026.map(a => (
              <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Lista Hierárquica */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {abordagemAtiva.categorias.map((cat) => (
            <div key={cat.titulo} className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
              <button 
                onClick={() => toggleCategoria(cat.titulo)}
                className="w-full flex items-center justify-between p-4 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <span className="text-blue-600 dark:text-blue-400 font-bold">{cat.titulo}</span>
                {expandidos[cat.titulo] ? <ChevronDown size={20}/> : <ChevronRight size={20}/>}
              </button>

              {expandidos[cat.titulo] && (
                <div className="bg-slate-50 dark:bg-slate-900 px-4 py-2 border-t border-slate-200 dark:border-slate-700">
                  {cat.topicos.map((topico) => {
                    const badge = getRevisionBadge(topico);
                    return (
                      <div key={topico} className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700 last:border-0">
                        <span className="text-slate-700 dark:text-slate-300 font-medium">• {topico}</span>
                        
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className={`text-xs font-bold ${badge.color}`}>
                            {badge.label}
                          </Badge>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleScheduleRevision(topico, cat.titulo)}
                            title="Agendar/Revisar"
                          >
                            <CalendarDays size={18} className="text-blue-600" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
